import { useState } from 'react';

export default function RegistrationSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    position: '',
    revenue: '',
    motivation: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        position: '',
        revenue: '',
        motivation: ''
      });

      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="bewerbung" className="py-16 sm:py-20 lg:py-32 bg-neutral-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <div className="inline-flex items-center gap-2 bg-yellow-600/20 text-yellow-600 px-4 sm:px-6 py-2 rounded-full mb-4 sm:mb-6 border border-yellow-600/30">
              <i className="ri-file-text-line text-lg sm:text-xl w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center"></i>
              <span className="font-light tracking-wide text-sm sm:text-base">Application Form</span>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl xl:text-6xl mb-4 sm:mb-6 text-white">
              Bewerbung für Mitgliedschaft
            </h2>
            <p className="text-base sm:text-lg text-white/70 font-light leading-relaxed max-w-2xl mx-auto px-4">
              Bitte füllen Sie das Formular vollständig aus. Wir melden uns innerhalb von 7 Werktagen persönlich bei Ihnen zurück.
            </p>
          </div>

          {/* Success Message */}
          {submitStatus === 'success' && (
            <div className="mb-8 p-6 sm:p-8 bg-green-500/10 border border-green-500/30 rounded-lg">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-green-500/20 rounded-full flex-shrink-0">
                  <i className="ri-check-line text-xl sm:text-2xl text-green-500"></i>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-light text-white mb-2">Bewerbung erfolgreich eingereicht</h3>
                  <p className="text-sm sm:text-base text-white/70 font-light">
                    Vielen Dank für Ihre Bewerbung. Wir prüfen Ihre Angaben sorgfältig und melden uns innerhalb von 7 Werktagen persönlich bei Ihnen. Bitte überprüfen Sie auch Ihren Spam-Ordner.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-neutral-950 p-6 sm:p-8 lg:p-12 border-t border-yellow-600/30">
            <div className="space-y-6 sm:space-y-8">
              {/* Row 1: Name & Email */}
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-white/80 mb-2 sm:mb-3 font-light text-sm sm:text-base">
                    Vollständiger Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-neutral-900 border border-white/10 text-white rounded-lg focus:outline-none focus:border-yellow-600/50 transition-colors font-light text-sm sm:text-base"
                    placeholder="Max Mustermann"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-white/80 mb-2 sm:mb-3 font-light text-sm sm:text-base">
                    E-Mail-Adresse *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-neutral-900 border border-white/10 text-white rounded-lg focus:outline-none focus:border-yellow-600/50 transition-colors font-light text-sm sm:text-base"
                    placeholder="max@beispiel.de"
                  />
                </div>
              </div>

              {/* Row 2: Phone */}
              <div>
                <label htmlFor="phone" className="block text-white/80 mb-2 sm:mb-3 font-light text-sm sm:text-base">
                  Telefonnummer *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-neutral-900 border border-white/10 text-white rounded-lg focus:outline-none focus:border-yellow-600/50 transition-colors font-light text-sm sm:text-base"
                  placeholder="+49 123 456789"
                />
              </div>

              {/* Row 3: Company & Position */}
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="company" className="block text-white/80 mb-2 sm:mb-3 font-light text-sm sm:text-base">
                    Unternehmen *
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    required
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-neutral-900 border border-white/10 text-white rounded-lg focus:outline-none focus:border-yellow-600/50 transition-colors font-light text-sm sm:text-base"
                    placeholder="Firmenname GmbH"
                  />
                </div>

                <div>
                  <label htmlFor="position" className="block text-white/80 mb-2 sm:mb-3 font-light text-sm sm:text-base">
                    Position *
                  </label>
                  <input
                    type="text"
                    id="position"
                    name="position"
                    required
                    value={formData.position}
                    onChange={handleChange}
                    className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-neutral-900 border border-white/10 text-white rounded-lg focus:outline-none focus:border-yellow-600/50 transition-colors font-light text-sm sm:text-base"
                    placeholder="Geschäftsführer"
                  />
                </div>
              </div>

              {/* Row 4: Revenue */}
              <div>
                <label htmlFor="revenue" className="block text-white/80 mb-2 sm:mb-3 font-light text-sm sm:text-base">
                  Jährlicher Umsatz (ca.) *
                </label>
                <select
                  id="revenue"
                  name="revenue"
                  required
                  value={formData.revenue}
                  onChange={handleChange}
                  className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-neutral-900 border border-white/10 text-white rounded-lg focus:outline-none focus:border-yellow-600/50 transition-colors font-light text-sm sm:text-base"
                >
                  <option value="">Bitte auswählen</option>
                  <option value="under-100k">Unter 100.000 €</option>
                  <option value="100k-250k">100.000 € - 250.000 €</option>
                  <option value="250k-500k">250.000 € - 500.000 €</option>
                  <option value="500k-1m">500.000 € - 1 Mio. €</option>
                  <option value="1m-5m">1 Mio. € - 5 Mio. €</option>
                  <option value="over-5m">Über 5 Mio. €</option>
                </select>
              </div>

              {/* Row 5: Motivation */}
              <div>
                <label htmlFor="motivation" className="block text-white/80 mb-2 sm:mb-3 font-light text-sm sm:text-base">
                  Warum möchten Sie Mitglied werden? *
                </label>
                <textarea
                  id="motivation"
                  name="motivation"
                  required
                  value={formData.motivation}
                  onChange={handleChange}
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-neutral-900 border border-white/10 text-white rounded-lg focus:outline-none focus:border-yellow-600/50 transition-colors font-light resize-none text-sm sm:text-base"
                  placeholder="Bitte beschreiben Sie Ihre Motivation und was Sie vom Dubai Business Club erwarten..."
                />
                <div className="text-white/40 text-xs sm:text-sm mt-2 font-light">
                  {formData.motivation.length}/500 Zeichen
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-yellow-600/10 border border-yellow-600/30 p-4 sm:p-6 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-yellow-600/20 rounded-full flex-shrink-0 mt-0.5">
                    <i className="ri-information-line text-yellow-600 text-base sm:text-lg"></i>
                  </div>
                  <div className="text-white/70 font-light text-xs sm:text-sm leading-relaxed">
                    <strong className="text-white font-normal">Wichtiger Hinweis:</strong> Der Dubai Business Club ist exklusiv und zugangsbasiert. Wir prüfen jede Bewerbung individuell und entscheiden nach einem persönlichen Gespräch über die Aufnahme.
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 sm:gap-3 px-8 sm:px-12 py-4 sm:py-5 bg-yellow-600 text-black rounded-full hover:bg-yellow-500 transition-all duration-300 transform hover:scale-105 shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 group whitespace-nowrap cursor-pointer text-sm sm:text-base font-light tracking-wide"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-lg sm:text-xl"></i>
                      <span>Wird gesendet...</span>
                    </>
                  ) : (
                    <>
                      <span>Bewerbung absenden</span>
                      <i className="ri-send-plane-line text-lg sm:text-xl group-hover:translate-x-1 transition-transform"></i>
                    </>
                  )}
                </button>
              </div>

              {/* Privacy Note */}
              <p className="text-white/50 text-xs sm:text-sm font-light text-center leading-relaxed">
                Mit dem Absenden bestätigen Sie, dass Sie unsere{' '}
                <a href="/datenschutz" className="text-yellow-600 hover:text-yellow-500 transition-colors cursor-pointer">
                  Datenschutzerklärung
                </a>{' '}
                gelesen haben und der Verarbeitung Ihrer Daten zustimmen.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
